/**
 * Human-readable labels for audit records — the single source of truth.
 *
 * ADR-0035 §3: these pure-string functions previously lived in
 * `apps/dashboard/src/views/mandates/intent-label.ts`. The dashboard's
 * MandateDetail panel and the bundle route's HTML rendering both project
 * the same audit-record shape into human-readable text; keeping the maps
 * in one package structurally prevents drift between the two surfaces.
 *
 * The module is TypeScript-only (no React, no lucide-react). The icon
 * functions (`providerIcon`, `visitorClassIcon`) and the `RULE_ACTION_TOOLTIP`
 * map stay in the dashboard view directory because they take a React /
 * lucide-react dependency that has no business inside `packages/audit/`.
 *
 * No runtime side effects on import; all symbols are pure functions or
 * frozen string maps.
 */
import { BUNDLED_AGENT_IDS, isSillFirstPartySigner, publicSignerDisplayFor, SILL_INTERNAL_AGENT_IDS, SILL_INTERNAL_TOOLS } from '@sill/types';
import { humanRecordName } from './human-visitor-label.js';
/**
 * Founder smoke-test agent id. Single source of truth for the
 * `agent_id` value the johnnyvbt smoke harness emits. Exported so the
 * cross-tenant admin metrics rollup (services/api `src/metrics/rollup.ts`)
 * can exclude smoke traffic from honest activity counts without
 * inlining the literal string in five places.
 *
 * MIRRORS the registry classifier in `apps/edge/src/identity/registry.ts`
 * + the friendly label below in `AGENT_DISPLAY_NAME` /
 * `AGENT_BRAND_NAME`. A rename of this slug would silently bypass the
 * smoke-exclusion in the rollup — the constant is the structural pin.
 */
export const AGENT_SILL_SMOKE = 'agent_sill_smoke';
/**
 * `agent_id` → marketing-friendly display name. Single source of truth
 * for the Mandates list view's matched-agent pill, the MandateDetail
 * panel, the `recordHeadline()` search index, AND the bundle HTML's
 * record headlines (ADR-0035 §3). Mirrors the BUNDLED_REGISTRY in
 * `apps/edge/src/identity/registry.ts` but kept here because the
 * `/v1/audit` wire shape is just the `agent_id` slug.
 */
export const AGENT_DISPLAY_NAME = {
    agent_anthropic_claude: 'Anthropic · ClaudeBot',
    agent_anthropic_claude_user: 'Anthropic · Claude user browse',
    agent_openai_gpt: 'OpenAI · GPTBot',
    agent_openai_chatgpt_user: 'OpenAI · ChatGPT user browse',
    agent_openai_chatgpt: 'OpenAI · ChatGPT mobile app',
    agent_openai_searchbot: 'OpenAI · ChatGPT Search',
    agent_google_gemini: 'Google · Gemini (Google-Extended)',
    agent_perplexity: 'Perplexity',
    // 2026-06-01 expansion — see ADR follow-up. Names match the registry's
    // organization + UA prefix.
    agent_microsoft_bingbot: 'Microsoft · Bingbot',
    agent_meta_externalagent: 'Meta · External Agent (training)',
    agent_meta_externalfetcher: 'Meta · External Fetcher (user)',
    agent_apple_extended: 'Apple · Applebot-Extended',
    agent_duckduckgo_assistbot: 'DuckDuckGo · DuckAssist',
    agent_mistral_user: 'Mistral · Le Chat user',
    // Social link-preview bots (not AI agents, but common visitors).
    agent_x_twitterbot: 'X · Twitterbot',
    agent_meta_facebook_external: 'Meta · facebookexternalhit',
    agent_meta_facebot: 'Meta · Facebot',
    agent_linkedin_bot: 'LinkedIn · LinkedInBot',
    agent_slack_link_expander: 'Slack · link unfurl',
    agent_discord_bot: 'Discord · link preview',
    // SEO / web-index bots captured by AI_CRAWLERS (not AI agents, but
    // common security-scan / indexing visitors). ADR-0203 §display.
    agent_mj12bot: 'Majestic · MJ12bot',
    agent_ahrefsbot: 'Ahrefs · AhrefsBot',
    agent_semrushbot: 'Semrush · SemrushBot',
    agent_dotbot: 'OpenLinkProfiler · DotBot',
    agent_yandexbot: 'Yandex · YandexBot',
    agent_baiduspider: 'Baidu · BaiduSpider',
    agent_sogou: 'Sogou · SogouSpider',
    agent_bytespider: 'ByteDance · Bytespider',
    agent_ccbot: 'Common Crawl · CCBot',
    agent_amazonbot: 'Amazon · AmazonBot',
    agent_petalbot: 'Huawei · PetalBot',
    agent_diffbot: 'Diffbot · Diffbot',
    agent_ia_archiver: 'Internet Archive · ia_archiver',
    // Search crawlers added 2026-08-26 (registry expansion).
    agent_google_searchbot: 'Google · Googlebot',
    agent_commoncrawl: 'Common Crawl · CCBot',
    agent_reflection_bot: 'Reflection AI · Reflectionbot',
    // Registry expansion coverage — mirror the entries bundled in
    // `apps/edge/src/identity/registry.ts` so the dashboard / bundle do
    // not fall back to a raw slug for a known agent.
    agent_anthropic_claude_searchbot: 'Anthropic · Claude SearchBot',
    agent_openai_adsbot: 'OpenAI · OAI-AdsBot',
    agent_perplexity_user: 'Perplexity · user browse',
    agent_meta_webindexer: 'Meta · WebIndexer',
    agent_meta_externalads: 'Meta · ExternalAds',
    agent_sill_heartbeat: 'Sill · Heartbeat',
    // ADR-0253 2026-10-27 — first-party AP2 test agent (ops2026 Protocol
    // Test Agents "Pay held order" flow). Only ingests whose SD-JWT
    // cnf.jwk thumbprint matches the configured ops public-key
    // thumbprint land on this id; real third-party AP2 holders are
    // opaque `agent_ap2_<hash>`.
    agent_sill_ops_ap2_test: 'Sill · ops AP2 test',
    // First-party UCP test platform (requests signed by sill.so keys).
    agent_sill_ucp_test: 'Sill · UCP test',
    // ADR-0229 registry additions.
    agent_amazon_searchbot: 'Amazon · Amzn-SearchBot',
    agent_amazon_user: 'Amazon · Amzn-User',
    agent_exa_searchbot: 'Exa · ExaSearchBot',
    agent_linkup_bot: 'Linkup · LinkupBot',
    agent_parallel_shapbot: 'Parallel · ShapBot',
    agent_parallel_shap_user: 'Parallel · Shap-User',
    agent_moonshot_kimibot: 'Moonshot · KimiBot',
    agent_moonshot_kimi_searchbot: 'Moonshot · Kimi-SearchBot',
    agent_moonshot_kimi_user: 'Moonshot · Kimi-User',
    agent_you_youbot: 'You.com · YouBot',
    agent_sill_test_fixture: 'Sill · test fixture',
    // GitHub infrastructure.
    agent_github_camo: 'GitHub · Camo image proxy',
    // Social / brand monitoring crawlers.
    agent_awario_bot: 'Awario · AwarioBot',
    agent_brand_verity: 'BrandVerity · brand monitor',
    agent_mention_bot: 'Mention · MentionBot',
    // Uptime / monitoring services.
    agent_uptimerobot: 'UptimeRobot · monitor',
    agent_pingdom: 'Pingdom · monitor',
    agent_statuscake: 'StatusCake · monitor',
    // SEO / analytics (extended).
    agent_dataforseo_bot: 'DataForSeo · DataForSeoBot',
    agent_sistrix: 'Sistrix · crawler',
    agent_magpie: 'Magpie · crawler',
    agent_screaming_frog: 'Screaming Frog · SEO spider',
    agent_site_audit_bot: 'SiteAuditBot · SEO crawler',
    // Search engines (extended).
    agent_apple_applebot: 'Apple · Applebot',
    agent_duckduckgo_searchbot: 'DuckDuckGo · DuckDuckBot',
    agent_google_image_bot: 'Google · Image crawler',
    agent_google_video_bot: 'Google · Video crawler',
    agent_google_inspection_tool: 'Google · Inspection tool',
    agent_google_news_bot: 'Google · News crawler',
    // Programmatic HTTP clients (display name = tool name; no org prefix so
    // the headline reads "Likely curl" not "Likely HTTP client").
    agent_curl: 'curl',
    agent_wget: 'wget',
    agent_python_requests: 'python-requests',
    agent_go_http: 'Go net/http',
    // 2026-06-18 founder test agent — the johnnyvbt smoke key. Surfaces
    // under the friendly display name on the dashboard / bundle when
    // smoke-tests fire through the live wire.
    agent_sill_smoke: 'Sill · smoke test',
    // ADR-0230 — Sill's own tools (bundled internal identities).
    // 2026-10-12 — "Sill · Site scanner": headlines keep the full name for
    // these (see `headlineAgentName`); the purpose line says "Sill (internal)".
    ...Object.fromEntries(SILL_INTERNAL_TOOLS.map((t) => [t.agent_id, `Sill · ${t.name}`])),
};
/**
 * One-sentence description of what each known agent was doing when it
 * visited. Shown in the MandateDetail panel's "Activity" row to give
 * merchants a plain-language answer to "why did this agent come here?"
 *
 * Three categories of visit:
 *   - "Search indexing" — crawler building/refreshing a search index.
 *   - "Training crawl"  — crawler collecting data for model training.
 *   - "User browse"     — real end-user triggered the request via an
 *                         AI assistant (your page was surfaced in a search
 *                         or the user asked the assistant to read it).
 */
export const AGENT_ACTIVITY = {
    agent_anthropic_claude: 'Training / search crawl — Anthropic is indexing your content for Claude.',
    agent_anthropic_claude_user: 'User browse — a Claude user asked Claude to access your page.',
    agent_openai_gpt: 'Training crawl — OpenAI GPTBot is crawling your pages for model training.',
    agent_openai_chatgpt_user: 'User browse — a ChatGPT user browsed your site via ChatGPT web access.',
    agent_openai_chatgpt: 'User browse — triggered by the ChatGPT mobile app reading your page.',
    agent_openai_searchbot: 'Search indexing — OpenAI is crawling your pages for ChatGPT Search results.',
    // ADR-0229 — robots.txt-only tokens: they never crawl. Kept for records
    // ingested before 2026-10-06, which were impersonators using the token.
    agent_google_gemini: 'Google-Extended is a robots.txt setting, not a crawler. A visit using this name was not from Google.',
    agent_perplexity: 'Search indexing — Perplexity is indexing your content for its AI search.',
    agent_microsoft_bingbot: 'Search indexing — Microsoft Bingbot is crawling for Bing and Copilot.',
    agent_meta_externalagent: 'Training crawl — Meta is crawling your pages for AI model training.',
    agent_meta_externalfetcher: 'User browse — a Meta AI user action fetched your page.',
    agent_apple_extended: 'Applebot-Extended is a robots.txt setting, not a crawler. A visit using this name was not from Apple.',
    agent_duckduckgo_assistbot: 'Search / AI assist — DuckDuckGo DuckAssist is crawling your pages.',
    agent_mistral_user: 'User browse — a Le Chat (Mistral) user accessed your page.',
    // SEO / web-index bots — not AI agents, but captured by the AI crawler list.
    agent_mj12bot: 'SEO crawl — Majestic MJ12bot is indexing your backlink profile.',
    agent_ahrefsbot: 'SEO crawl — Ahrefs is indexing your site for backlink and SEO analysis.',
    agent_semrushbot: 'SEO crawl — Semrush is crawling your pages for SEO intelligence.',
    agent_dotbot: 'SEO crawl — OpenLinkProfiler DotBot is indexing your outbound links.',
    agent_yandexbot: 'Search indexing — Yandex search engine is crawling your content.',
    agent_baiduspider: 'Search indexing — Baidu search engine is crawling your content.',
    agent_sogou: 'Search indexing — Sogou (Tencent) search is crawling your content.',
    agent_bytespider: 'Crawl — ByteDance (TikTok parent) is crawling your content.',
    agent_ccbot: 'Training crawl — Common Crawl is archiving your pages for open datasets.',
    agent_amazonbot: 'Crawl — Amazon is crawling your content (Alexa / product discovery).',
    agent_petalbot: 'Search indexing — Huawei PetalBot is crawling for Petal Search.',
    agent_diffbot: 'Structured extraction — Diffbot is parsing your pages into structured data.',
    agent_ia_archiver: 'Archiving — Internet Archive (Wayback Machine) is preserving your pages.',
    agent_google_searchbot: 'Search indexing — Googlebot is crawling your content for Google Search.',
    agent_commoncrawl: 'Training crawl — Common Crawl is archiving your pages for open datasets.',
    agent_reflection_bot: 'Training / research crawl — Reflection AI is crawling your content.',
    // GitHub infrastructure.
    agent_github_camo: 'Image proxy — GitHub fetched this image because someone referenced it in a GitHub repository, issue, or pull request. GitHub proxies external images through Camo to protect its users. Safe infrastructure activity.',
    // Social / brand monitoring crawlers.
    agent_awario_bot: 'Brand monitoring — Awario is crawling your pages to detect mentions of tracked keywords for one of its customers.',
    agent_brand_verity: 'Brand monitoring — BrandVerity is checking your pages for trademark or brand compliance on behalf of a brand owner.',
    agent_mention_bot: 'Brand monitoring — Mention is crawling your pages to track brand mentions for one of its customers.',
    // Uptime / monitoring services.
    agent_uptimerobot: 'Uptime monitor — UptimeRobot is pinging your site to check it is reachable. Someone has added your URL to a monitoring check.',
    agent_pingdom: 'Uptime monitor — Pingdom is checking your site availability. Someone has configured a Pingdom monitor for your URL.',
    agent_statuscake: 'Uptime monitor — StatusCake is testing your site availability on behalf of a monitor subscriber.',
    // SEO / analytics (extended).
    agent_dataforseo_bot: 'SEO data crawl — DataForSeo is collecting search-ranking and page-data signals for its customers.',
    agent_sistrix: 'SEO crawl — Sistrix is indexing your site for SEO visibility tracking.',
    agent_magpie: 'SEO crawl — Magpie Crawler is indexing your pages for SEO analysis.',
    agent_screaming_frog: 'SEO audit — Screaming Frog SEO Spider is crawling your site, likely triggered by a manual audit.',
    agent_site_audit_bot: 'SEO crawl — an automated site audit tool is checking your pages for SEO signals.',
    // Search engines (extended).
    agent_apple_applebot: 'Search indexing — Apple is crawling your content for Siri and Spotlight search.',
    agent_duckduckgo_searchbot: 'Search indexing — DuckDuckGo is crawling your pages for its search index.',
    agent_google_image_bot: 'Image indexing — Google is crawling images on your pages for Google Image Search.',
    agent_google_video_bot: 'Video indexing — Google is indexing videos on your pages for Google Video Search.',
    agent_google_inspection_tool: 'Site testing — Google Search Console or the Rich Results Test is fetching a page on request.',
    agent_google_news_bot: 'News indexing — Google News crawler is checking your pages for news-eligible content.',
    // ADR-0229 registry additions.
    agent_amazon_searchbot: 'Search indexing — Amazon is indexing your pages for its search and assistant answers.',
    agent_amazon_user: 'User browse — an Amazon assistant user asked for your page.',
    agent_exa_searchbot: 'Search indexing — Exa is indexing your pages for its AI search API.',
    agent_linkup_bot: 'Search indexing — Linkup is indexing your pages for its AI search API.',
    agent_parallel_shapbot: 'Search indexing — Parallel is indexing your pages for its web search API for AI agents.',
    agent_parallel_shap_user: 'User browse — an AI agent using Parallel fetched your page.',
    agent_moonshot_kimibot: 'Training crawl — Moonshot AI is crawling your pages for Kimi model training.',
    agent_moonshot_kimi_searchbot: 'Search indexing — Moonshot AI is indexing your pages for Kimi search.',
    agent_moonshot_kimi_user: 'User browse — a Kimi user asked Kimi to read your page.',
    agent_you_youbot: 'Search indexing — You.com is indexing your pages for its AI search.',
    // Programmatic HTTP clients.
    agent_curl: 'HTTP tool — curl made a request to your site. This is a command-line HTTP client, usually triggered by a developer, script, or automation pipeline.',
    agent_wget: 'HTTP tool — wget fetched a resource from your site. This is a command-line download tool, usually a developer or automated script.',
    agent_python_requests: 'HTTP tool — a Python script using the requests library accessed your site. Likely a developer tool, scraper, or automation.',
    agent_go_http: 'HTTP tool — a Go program made a request to your site via the standard net/http client. Likely a developer tool or backend integration.',
    // ADR-0230 — Sill's own tools.
    ...Object.fromEntries(SILL_INTERNAL_TOOLS.map((t) => [t.agent_id, t.merchant_purpose])),
};
/**
 * `agent_id` → clean BRAND-ONLY label (e.g. `ChatGPT`, `Claude`,
 * `Gemini`). Used by row cards and table cells where the long
 * "Org · Product" form reads as a registry dump. Founder-locked
 * 2026-06-18: an Owner glancing at the Signed Transactions table
 * should see `ChatGPT`, not `CHATGPT MOBILE APP` or a raw slug.
 *
 * Keep additive: every key here must also appear in
 * `AGENT_DISPLAY_NAME` (the registry-style long form stays the
 * source-of-truth for the MandateDetail panel and the bundle).
 */
export const AGENT_BRAND_NAME = {
    agent_anthropic_claude: 'Claude',
    agent_anthropic_claude_user: 'Claude',
    agent_openai_gpt: 'GPTBot',
    agent_openai_chatgpt: 'ChatGPT',
    agent_openai_chatgpt_user: 'ChatGPT',
    agent_openai_searchbot: 'ChatGPT Search',
    agent_google_gemini: 'Gemini',
    agent_perplexity: 'Perplexity',
    agent_microsoft_bingbot: 'Bingbot',
    agent_meta_externalagent: 'Meta agent',
    agent_meta_externalfetcher: 'Meta fetcher',
    agent_apple_extended: 'Applebot',
    agent_duckduckgo_assistbot: 'DuckAssist',
    agent_mistral_user: 'Le Chat',
    agent_x_twitterbot: 'Twitterbot',
    agent_meta_facebook_external: 'Facebook',
    agent_meta_facebot: 'Facebot',
    agent_linkedin_bot: 'LinkedInBot',
    agent_slack_link_expander: 'Slack',
    agent_discord_bot: 'Discord',
    agent_sill_smoke: 'Sill smoke test',
    // Registry expansion — brand-only cells for the Signed Transactions
    // table + row cards. Mirrors the long-form entries above.
    agent_anthropic_claude_searchbot: 'Claude SearchBot',
    agent_openai_adsbot: 'OAI-AdsBot',
    agent_perplexity_user: 'Perplexity user',
    agent_meta_webindexer: 'WebIndexer',
    agent_meta_externalads: 'Meta ads',
    agent_google_searchbot: 'Googlebot',
    agent_commoncrawl: 'CCBot',
    agent_reflection_bot: 'Reflectionbot',
    agent_sill_heartbeat: 'Sill Heartbeat',
    // ADR-0253 2026-10-27 — first-party AP2 test agent.
    agent_sill_ops_ap2_test: 'Sill ops AP2 test',
    agent_sill_ucp_test: 'Sill UCP test',
    // ADR-0229 registry additions.
    agent_amazon_searchbot: 'Amzn-SearchBot',
    agent_amazon_user: 'Amzn-User',
    agent_exa_searchbot: 'Exa',
    agent_linkup_bot: 'LinkupBot',
    agent_parallel_shapbot: 'ShapBot',
    agent_parallel_shap_user: 'Shap-User',
    agent_moonshot_kimibot: 'KimiBot',
    agent_moonshot_kimi_searchbot: 'Kimi-SearchBot',
    agent_moonshot_kimi_user: 'Kimi-User',
    agent_you_youbot: 'YouBot',
    agent_sill_test_fixture: 'Sill test fixture',
    // SEO / web-index bots.
    agent_mj12bot: 'MJ12bot',
    agent_ahrefsbot: 'AhrefsBot',
    agent_semrushbot: 'SemrushBot',
    agent_dotbot: 'DotBot',
    agent_yandexbot: 'YandexBot',
    agent_baiduspider: 'BaiduSpider',
    agent_sogou: 'Sogou',
    agent_bytespider: 'Bytespider',
    agent_ccbot: 'CCBot',
    agent_amazonbot: 'AmazonBot',
    agent_petalbot: 'PetalBot',
    agent_diffbot: 'Diffbot',
    agent_ia_archiver: 'Internet Archive',
    // GitHub infrastructure.
    agent_github_camo: 'GitHub Camo',
    // Social / brand monitoring.
    agent_awario_bot: 'Awario',
    agent_brand_verity: 'BrandVerity',
    agent_mention_bot: 'Mention',
    // Uptime / monitoring.
    agent_uptimerobot: 'UptimeRobot',
    agent_pingdom: 'Pingdom',
    agent_statuscake: 'StatusCake',
    // SEO / analytics (extended).
    agent_dataforseo_bot: 'DataForSeo',
    agent_sistrix: 'Sistrix',
    agent_magpie: 'Magpie',
    agent_screaming_frog: 'Screaming Frog',
    agent_site_audit_bot: 'SiteAuditBot',
    // Search engines (extended).
    agent_apple_applebot: 'Applebot',
    agent_duckduckgo_searchbot: 'DuckDuckBot',
    agent_google_image_bot: 'Google Images',
    agent_google_video_bot: 'Google Videos',
    agent_google_inspection_tool: 'Google Search Console',
    agent_google_news_bot: 'Google News',
    // Programmatic HTTP clients.
    agent_curl: 'curl',
    agent_wget: 'wget',
    agent_python_requests: 'python-requests',
    agent_go_http: 'Go net/http',
    // ADR-0230 — Sill's own tools.
    ...Object.fromEntries(SILL_INTERNAL_TOOLS.map((t) => [t.agent_id, `Sill · ${t.name}`])),
};
/**
 * Convert a raw `agent_xxx_yyy` slug into a human-readable token when
 * we have no registered display entry — strips the `agent_` prefix and
 * the redundant org prefix when the second segment repeats the first
 * (e.g. `agent_perplexity_perplexity` → `Perplexity`). Title-cases each
 * remaining segment. Used as the present-but-unknown fallback so we
 * surface a humanized slug instead of the SHOUTY_CAPS raw id.
 */
function humanizeAgentSlug(agent_id) {
    const tail = agent_id.startsWith('agent_') ? agent_id.slice(6) : agent_id;
    if (tail.length === 0)
        return agent_id;
    const parts = tail.split('_').filter((p) => p.length > 0);
    if (parts.length === 0)
        return agent_id;
    return parts
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
        .join(' ');
}
/**
 * Short BRAND-ONLY label for a row card / table cell.
 *
 *   - registered slug (e.g. `agent_openai_chatgpt`) → brand only (`ChatGPT`).
 *   - present-but-unknown slug                      → humanized slug
 *                                                     (`Agent Madeup`, not
 *                                                     `AGENT_MADEUP`).
 *   - `undefined`                                   → `'Unidentified agent'`.
 *
 * Founder-locked 2026-06-18: the Signed Transactions AGENT cell must
 * never render literal `'AGENT'`. The previous SHOUTY_CAPS contract is
 * gone — every surface in the dashboard now consumes this brand-clean
 * form.
 */
export function shortAgentDisplayName(agent_id) {
    if (agent_id === undefined)
        return 'Unidentified agent';
    const brand = AGENT_BRAND_NAME[agent_id];
    if (brand !== undefined)
        return brand;
    return humanizeAgentSlug(agent_id);
}
/**
 * Headline form of a registry name: the short product name after the org
 * prefix ("OpenAI · GPTBot" → "GPTBot"), except Sill's own tools, which
 * keep "Sill · Site scanner" so a merchant can tell Sill from a visitor.
 */
export function headlineAgentName(agent_id) {
    const full = displayAgentName(agent_id);
    if (agent_id !== undefined && SILL_INTERNAL_AGENT_IDS.has(agent_id))
        return full;
    return full.split(' · ').pop() ?? full;
}
export function displayAgentName(agent_id) {
    if (agent_id === undefined)
        return 'Unknown agent';
    const registered = AGENT_DISPLAY_NAME[agent_id];
    if (registered !== undefined)
        return registered;
    // Fallback: humanize the slug (e.g. `agent_acme_shopper` →
    // `Acme Shopper`) instead of surfacing the raw id. The dashboard /
    // bundle headlines strip the "Org · " prefix via `split(' · ').pop()`
    // when a registered entry carries one; humanized slugs have no prefix,
    // so the fallback renders directly.
    return humanizeAgentSlug(agent_id);
}
/**
 * Client-side best-effort identification by UA substring. Used to
 * surface "this UA looks like X" hints in the MandateDetail panel
 * for records the server-side classifier didn't match (most common
 * cause: the record was ingested BEFORE the agent's regex entry was
 * added to the registry — old visits don't get re-classified).
 *
 * Mirrors the canonical registry patterns in
 * `apps/edge/src/identity/registry.ts` so the dashboard says the
 * same thing the live classifier would say. Returns null when no
 * known agent token appears in the UA.
 */
const UA_TOKEN_TO_AGENT = [
    // Order matters — more-specific patterns first.
    // ADR-0230 — Sill's own tools (exact token + `/`, so order-free).
    ...SILL_INTERNAL_TOOLS.map((t) => [new RegExp(`\\b${t.token}/`, 'i'), t.agent_id]),
    [/\bChatGPT-User\//i, 'agent_openai_chatgpt_user'],
    [/\bChatGPT\//i, 'agent_openai_chatgpt'],
    [/\bGPTBot\//i, 'agent_openai_gpt'],
    [/\bOAI-SearchBot\//i, 'agent_openai_searchbot'],
    [/\bClaudeBot\//i, 'agent_anthropic_claude'],
    [/\bClaude-User\//i, 'agent_anthropic_claude_user'],
    // ADR-0229 — Google-Extended / Applebot-Extended are robots.txt tokens,
    // never real UAs; they are deliberately NOT mapped here.
    [/\bPerplexityBot\//i, 'agent_perplexity'],
    [/\bbingbot\//i, 'agent_microsoft_bingbot'],
    [/\bmeta-externalagent\//i, 'agent_meta_externalagent'],
    [/\bmeta-externalfetcher\//i, 'agent_meta_externalfetcher'],
    [/\bDuckAssistBot\//i, 'agent_duckduckgo_assistbot'],
    [/\bMistralAI-User\//i, 'agent_mistral_user'],
    // Social link-preview bots (added 2026-06-01).
    [/\bTwitterbot\//i, 'agent_x_twitterbot'],
    [/\bfacebookexternalhit\//i, 'agent_meta_facebook_external'],
    [/\bFacebot\b/i, 'agent_meta_facebot'],
    [/\bLinkedInBot\//i, 'agent_linkedin_bot'],
    [/\bSlackbot(?:-LinkExpanding)?[ /]/i, 'agent_slack_link_expander'],
    [/\bDiscordbot\//i, 'agent_discord_bot'],
    // SEO / web-index bots captured by AI_CRAWLERS but not AI agents — they
    // use the Mozilla/5.0 (compatible; BotName/…) pattern. Matched here so
    // the headline reads "MJ12bot" rather than "Unknown bot · Mozilla/5.0".
    [/\bMJ12bot\b/i, 'agent_mj12bot'],
    [/\bAhrefsBot\b/i, 'agent_ahrefsbot'],
    [/\bSemrushBot\b/i, 'agent_semrushbot'],
    [/\bDotBot\b/i, 'agent_dotbot'],
    [/\bYandexBot\b/i, 'agent_yandexbot'],
    [/\bBaiduSpider\b/i, 'agent_baiduspider'],
    // Sogou UA is "Sogou web spider/4.0(…)" — does NOT contain "SogouSpider".
    [/\bSogou\b/i, 'agent_sogou'],
    [/\bBytespider\b/i, 'agent_bytespider'],
    [/\bCCBot\b/i, 'agent_ccbot'],
    // Require /version after AmazonBot — "amazonbot" also appears as the last
    // path segment in the Amzn-SearchBot URL, so \bAmazonBot\b false-positives.
    [/\bAmazonBot\//i, 'agent_amazonbot'],
    [/\bPetalBot\b/i, 'agent_petalbot'],
    // ADR-0229 registry additions.
    [/\bAmzn-SearchBot\//i, 'agent_amazon_searchbot'],
    [/\bAmzn-User\//i, 'agent_amazon_user'],
    [/\bExaSearchBot\//i, 'agent_exa_searchbot'],
    [/\bLinkupBot\//i, 'agent_linkup_bot'],
    [/\bShapBot\//i, 'agent_parallel_shapbot'],
    [/\bShap-User\//i, 'agent_parallel_shap_user'],
    [/\bKimi-SearchBot\//i, 'agent_moonshot_kimi_searchbot'],
    [/\bKimi-User\//i, 'agent_moonshot_kimi_user'],
    [/\bKimiBot\//i, 'agent_moonshot_kimibot'],
    [/\bYouBot\//i, 'agent_you_youbot'],
    [/\bDiffBot\b/i, 'agent_diffbot'],
    [/\bia_archiver\b/i, 'agent_ia_archiver'],
    // GitHub infrastructure.
    [/\bgithub-camo\b/i, 'agent_github_camo'],
    // Social / brand monitoring crawlers.
    [/\bAwarioBot\b/i, 'agent_awario_bot'],
    [/\bBrandVerity\b/i, 'agent_brand_verity'],
    [/\bMentionBot\b/i, 'agent_mention_bot'],
    // Uptime / monitoring services.
    [/\bUptimeRobot\b/i, 'agent_uptimerobot'],
    [/\bPingdom\b/i, 'agent_pingdom'],
    [/\bStatusCake\b/i, 'agent_statuscake'],
    // SEO / analytics crawlers (extended set).
    [/\bDataForSeoBot\b/i, 'agent_dataforseo_bot'],
    [/\bSistrix\b/i, 'agent_sistrix'],
    [/\bMagpie-Crawler\b/i, 'agent_magpie'],
    [/\bScreamingFrog\b/i, 'agent_screaming_frog'],
    [/\bSiteAuditBot\b/i, 'agent_site_audit_bot'],
    // Search engines (extended).
    [/\bApplebot\//i, 'agent_apple_applebot'], // main Apple crawler (not the -Extended token)
    [/\bDuckDuckBot\b/i, 'agent_duckduckgo_searchbot'], // main DDG crawler (not DuckAssistBot)
    [/\bGooglebot-Image\b/i, 'agent_google_image_bot'],
    [/\bGooglebot-Video\b/i, 'agent_google_video_bot'],
    [/\bGoogle-InspectionTool\b/i, 'agent_google_inspection_tool'],
    [/\bGooglebot-News\b/i, 'agent_google_news_bot'],
    // Programmatic HTTP clients (not crawlers, but common unknown traffic).
    [/\bcurl\//i, 'agent_curl'],
    [/\bWget\//i, 'agent_wget'],
    [/\bpython-requests\b/i, 'agent_python_requests'],
    [/\bGo-http-client\b/i, 'agent_go_http'],
];
export function inferAgentFromUserAgent(user_agent) {
    if (user_agent === undefined || user_agent.length === 0)
        return null;
    for (const [re, agent_id] of UA_TOKEN_TO_AGENT) {
        if (re.test(user_agent))
            return agent_id;
    }
    return null;
}
const ACTION_FRIENDLY = {
    browse_catalog: 'Catalog browse',
    check_availability: 'Availability check',
    place_order: 'Order placement',
    order_status: 'Order status check',
    track_shipment: 'Shipment tracking',
    request_refund: 'Refund request',
    recommend: 'Recommendation',
    create_subscription: 'Subscription signup',
    cancel_subscription: 'Subscription cancellation',
    sign_up: 'Account signup',
    login: 'Login',
    add_to_cart: 'Cart addition',
};
function titleCaseFromSnake(s) {
    return s
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
export function friendlyActionLabel(action) {
    return ACTION_FRIENDLY[action] ?? titleCaseFromSnake(action);
}
/**
 * Humanize a snake_case refund reason code for display:
 * `wrong_item` → `Wrong item`. The enum is server-validated; this is a
 * display transform only (an unknown code degrades to the same shape).
 * Mirrors the dashboard `humanizeReasonCode` byte-for-byte so the
 * Resolution card and the Mandates card render the reason identically.
 */
export function humanizeReasonCode(code) {
    const cleaned = code.trim().replace(/[_-]+/g, ' ');
    if (cleaned.length === 0)
        return code;
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
}
/**
 * Format a MAJOR-unit currency amount for display. Phase 2 r05 uses
 * bare-dollar units (the rule's `max_amount_usd` is in whole USD;
 * `intent.max_amount` is in the same unit). Routes through
 * `Intl.NumberFormat` with the `en-US` locale so a four-figure amount
 * reads `$1,240.00`, not `$1240.00` — the single locale-aware
 * formatter every surface (Mandates panel, Resolution rows, Signed
 * Transactions, bundle HTML) consumes.
 *
 * USD renders with the `$` symbol prefix; non-USD renders with the
 * ISO 4217 code AFTER the amount (`1,240.00 EUR`), keeping the shape
 * every historic test / snapshot pinned.
 */
export function formatIntentAmount(amount, currency) {
    const safe = Number.isFinite(amount) ? amount : 0;
    if (currency === 'USD' || currency === '' || currency === undefined) {
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(safe);
        }
        catch {
            // Fall through to the manual path.
        }
    }
    const n = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(safe);
    return `${n} ${currency}`;
}
/**
 * Format an integer minor-unit amount (cents) + ISO 4217 currency code
 * into a buyer-facing string — the cross-cutting headline / search-index
 * equivalent of the dashboard's `formatMinor()` helper. Kept here so the
 * label module can render the actual settled-rail total without taking
 * a dashboard dep. Returns `null` for non-finite / non-integer / negative
 * inputs so the caller can fall back to the authorized-cap framing.
 *
 * Routes the dollar conversion through `formatIntentAmount` so the
 * thousands-separator + USD-symbol rules land in one place (a $10,000
 * settlement reads `$10,000.00`, not `$10000.00`).
 */
export function formatMinorAmount(minor, currency) {
    if (!Number.isFinite(minor)
        || !Number.isInteger(minor)
        || minor < 0) {
        return null;
    }
    return formatIntentAmount(minor / 100, currency);
}
/**
 * Pure non-negative-integer guard for a JSONB-projected amount. Matches
 * `isNonNegativeInteger` in `services/api/src/http/routes/v1/audit.ts`
 * byte-for-byte so the read paths apply the same validation rule.
 */
function isNonNegativeInt(v) {
    return (typeof v === 'number'
        && Number.isFinite(v)
        && Number.isInteger(v)
        && v >= 0);
}
/**
 * SINGLE SOURCE OF TRUTH for the "Revenue from agents" metric definition.
 *
 * Returns the order-settled amount in minor units for a settlement that
 * COUNTS as gross successful agent revenue, or `null` otherwise.
 *
 * Inclusion rule (founder-locked 2026-06-18):
 *   - `outcome === 'succeeded'` AND
 *   - `kind !== 'refund'` (an order settlement, NOT a refund execution)
 *
 * Amount field resolution order (writer wrote different keys at
 * different times — readers must accept both):
 *   1. `total_minor`  — canonical post-ADR-0063 order settlement
 *   2. `amount_minor` — legacy/pre-canonical Stripe rail (the charged
 *                       amount; equals the settled total on Stripe)
 *
 * Both keys are validated as non-negative integers. `refunded_minor` and
 * `original_total_minor` (refund fields) are NEVER consulted — they would
 * inflate the headline by the refund amount on a refund-settled mandate.
 *
 * EXPLICITLY EXCLUDED (return `null`):
 *   - Refund settlements (`kind === 'refund'`) — surfaced separately.
 *   - Failed / aborted / disputed / refunded / reconciling /
 *     approved_but_rail_disabled outcomes — never paid revenue.
 *   - Refund-mandate outcomes `reconciled` / `already_executed` —
 *     refund executions, not paid revenue.
 *   - Settlements with NO valid amount key.
 *
 * Rail-agnostic: this fn does NOT gate on the `rail` field. A successful
 * order settlement on a future rail (or a `rail=null` row written by the
 * pre-rail writer) is still revenue.
 *
 * LIVE-MODE GATE (opt-in, ADR-0140 honest-metrics follow-up): when
 * `{ liveOnly: true }` is passed, the function additionally requires
 * the row to be LIVE-mode on the settled rail — either
 * `stripe_mode === 'live'` (Stripe settlements) or
 * `shopify_mode === 'live'` (Shopify settlements). Test-mode rows
 * (mode === 'test') return `null`. The mode keys are denormalized into
 * the same `settlement` JSON object by the rail writer (see audit
 * envelope discovery_context.settlement); they are the reliable
 * real-vs-test signal for transactions/volume because there is no
 * separate `dry_run` flag or account-level test flag in the schema.
 *
 * Default (`liveOnly` absent / false) preserves the existing behaviour
 * — every other reader that wants both test + live continues to work
 * byte-for-byte. The SQL aggregations in the rollup compute mirror the
 * `liveOnly: true` branch (live-mode predicate inline); the per-site
 * reporting routes can opt in later by passing `liveOnly` to the
 * helper or by adding the same predicate to their SQL.
 *
 * Defensive `unknown` reader — `settlement` is the dashboard-local wire
 * extension on `AuditRecord`. The exported function is consumed by the
 * SQL aggregations (mirrored into SQL by the reporting routes) and by
 * any in-process audit-record reader that needs the headline revenue
 * figure.
 */
export function succeededOrderAmountMinor(settlement, options) {
    if (settlement === null || typeof settlement !== 'object')
        return null;
    const s = settlement;
    if (s['outcome'] !== 'succeeded')
        return null;
    if (s['kind'] === 'refund')
        return null;
    if (options?.liveOnly === true) {
        // Live iff EITHER rail wrote a 'live' mode key. A row missing both
        // keys is NOT live (the pre-mode-instrumentation rows fall out of
        // the live-only branch by construction).
        const stripe_mode = s['stripe_mode'];
        const shopify_mode = s['shopify_mode'];
        const is_live = (typeof stripe_mode === 'string' && stripe_mode === 'live')
            || (typeof shopify_mode === 'string' && shopify_mode === 'live');
        if (!is_live)
            return null;
    }
    const total = s['total_minor'];
    if (isNonNegativeInt(total))
        return total;
    const amount = s['amount_minor'];
    if (isNonNegativeInt(amount))
        return amount;
    return null;
}
/**
 * Read the actual settled total off a wire audit record. Returns a
 * formatted buyer-facing string ("$21.71" / "21.71 EUR") OR `null`
 * when the record carries no recognizable settled amount.
 *
 * Used by the Signed Transactions panel — a TRANSACTION LIST that
 * surfaces order settlements AND refund executions alike (the revenue
 * METRIC is a strict subset, sourced from `succeededOrderAmountMinor`).
 *
 * Amount-key resolution (in priority order — first match wins):
 *   1. `total_minor`    — canonical order settled total
 *   2. `amount_minor`   — legacy/pre-canonical Stripe rail
 *   3. `refunded_minor` — refund settlements (`kind === 'refund'`),
 *                         so the panel shows the refunded total
 *
 * Rail-agnostic — non-payment rails (forward-compat values like
 * `null` on the pre-rail writer) still render their settled total.
 * Pre-2026-06-18 this function gated on rail ∈ {stripe, shopify} AND
 * required `total_minor`; both gates rejected the actual production
 * settlement shapes (`rail=null` + `amount_minor`, or `kind='refund'`
 * + `refunded_minor`) and the panel rendered empty.
 *
 * Defensive reader through `unknown` because `settlement` lives on the
 * dashboard-local `MandateRecord` wire extension, NOT on the canonical
 * signed `AuditRecord` interface (the JSONB is outside the signing
 * scope — see ADR-0035, ADR-0063). The function tolerates legacy
 * records that pre-date the field.
 */
export function settledTotalForHeadline(record) {
    const settlement = record.settlement;
    if (settlement === null || typeof settlement !== 'object')
        return null;
    const s = settlement;
    // Amount key resolution: total_minor → amount_minor → refunded_minor.
    // The first non-negative integer found wins. (See module-level docstring
    // on the founder-locked revenue metric for why the METRIC consults only
    // total_minor??amount_minor; the LIST surface shows refund amounts too.)
    let minor = null;
    const total = s['total_minor'];
    if (isNonNegativeInt(total)) {
        minor = total;
    }
    else {
        const amount = s['amount_minor'];
        if (isNonNegativeInt(amount)) {
            minor = amount;
        }
        else {
            const refunded = s['refunded_minor'];
            if (isNonNegativeInt(refunded)) {
                minor = refunded;
            }
        }
    }
    if (minor === null)
        return null;
    const currency_raw = s['currency'];
    const currency = typeof currency_raw === 'string' && currency_raw.length > 0
        ? currency_raw
        : 'USD';
    return formatMinorAmount(minor, currency);
}
/**
 * The closed set of skills that do NOT move money and therefore do not
 * carry a sensible dollar figure in their row headline. The previous
 * contract appended `· $0.00` on every intent — a `check_availability`
 * row then read "Availability check · $0.00", which conflates "no
 * money" with "zero-dollar transaction". For these actions the headline
 * stops at the friendly action label. A money action (`place_order`,
 * `create_subscription`, `request_refund`) always renders the amount —
 * a $0.00 subscription is still a legitimate money event (free trial)
 * and keeps the explicit zero so the reader sees "Subscription signup ·
 * $0.00", not an implicit absence.
 */
const AMOUNTLESS_ACTIONS = new Set([
    'browse_catalog',
    'check_availability',
    'order_status',
    'track_shipment',
    'recommend',
    'login',
    'sign_up',
    'cancel_subscription',
    'add_to_cart',
]);
/**
 * Short summary line for a card: "Subscription signup · $0.00".
 *
 * When `amount_override` is supplied (typically the settled-rail total),
 * it replaces the intent's `max_amount` in the rendered string. This is
 * the load-bearing fix for the "headline shows $50 cap, settlement panel
 * shows $21.71 actual" discrepancy — the card and the detail header both
 * call `summarizeIntent(intent, settledTotal)` so the two views never
 * disagree on what dollar figure to lead with.
 *
 * For actions in {@link AMOUNTLESS_ACTIONS} the amount segment is
 * dropped entirely — a `check_availability` card renders as
 * `Availability check`, not `Availability check · $0.00`.
 */
export function summarizeIntent(intent, amount_override) {
    const action = friendlyActionLabel(intent.action);
    // Drop the amount segment when the action is categorically amount-less
    // AND the caller did NOT explicitly pass a string override. A `null`
    // override means "no settlement surfaced, use the intent's cap" — for
    // an amount-less action we still want to drop the segment; only an
    // explicit string override forces the amount back on (settlement
    // surfacing on an amount-less skill, hypothetical).
    if ((amount_override === undefined || amount_override === null)
        && AMOUNTLESS_ACTIONS.has(intent.action)) {
        return action;
    }
    const amount = amount_override ?? formatIntentAmount(intent.max_amount, intent.currency);
    return `${action} · ${amount}`;
}
/**
 * Longer summary for the detail panel:
 *   "Subscription signup at sill-newsletter · WEEKLY-DIGEST · $0.00 USD".
 */
export function describeIntent(intent) {
    const action = friendlyActionLabel(intent.action);
    const amount = formatIntentAmount(intent.max_amount, intent.currency);
    // ADR-0077 — `sku` is ONE-OF with `line_items`. Render the legacy
    // single SKU when present; otherwise render the multi-line cart's
    // SKUs (bounded ≤ MAX_LINE_ITEMS; non-PII catalogue identifiers).
    // Never interpolate a raw `undefined` into the label, and drop the
    // SKU segment entirely when neither shape is present.
    const item_label = describeIntentItems(intent);
    return item_label.length > 0
        ? `${action} at ${intent.merchant} · ${item_label} · ${amount}`
        : `${action} at ${intent.merchant} · ${amount}`;
}
/**
 * ADR-0077 — render the intent's item identifier(s) for a label. Legacy
 * single-SKU → the SKU; multi-line → the first two SKUs followed by
 * `+N more` when the cart has more than two lines (keeps the label
 * bounded regardless of cart size). Empty string when neither shape is
 * present (a malformed/legacy summary).
 */
function describeIntentItems(intent) {
    if (typeof intent.sku === 'string' && intent.sku.length > 0) {
        return intent.sku;
    }
    const lines = intent.line_items;
    if (lines !== undefined && lines.length > 0) {
        if (lines.length <= 2) {
            return lines.map((l) => l.sku).join(', ');
        }
        return `${lines[0].sku}, ${lines[1].sku} +${lines.length - 2} more`;
    }
    return '';
}
/**
 * Outcome-class label used in the row headline fallback ("Mandate
 * approved", "Mandate rejected", "Mandate escalated"). Mirrors the
 * coarse-grained pill the row renders, NOT the full decision enum.
 */
export function outcomeLabel(decision) {
    switch (decision) {
        case 'observed': return 'observed';
        case 'approved': return 'approved';
        case 'rejected':
        case 'verification_rejected':
        case 'rejected_post_verify':
            return 'rejected';
        case 'escalated_approved':
        case 'escalated_rejected':
            return 'escalated';
        // ADR-0157 (Bucket B Slice 2) — RAIL-NEUTRAL settlement outcome.
        case 'settled_succeeded': return 'settled';
        case 'settled_refunded': return 'refunded';
        // ADR-0174 §2 (Slice 1) — WooCommerce PAYMENT-LINK HANDOFF outcome.
        // First non-settlement transactional outcome — categorically NOT
        // `'settled'`. The buyer must still complete payment on the
        // merchant's own site; the audit surfaces this as `'handoff'` so
        // dashboard + bundle render honestly distinguish "money moved" from
        // "authorized-then-handed-off".
        case 'handoff_pending': return 'handoff';
    }
}
/**
 * Technical browser label with the major version ("Browser visit ·
 * Chrome 137"). NOT a merchant-facing name any more (founder 2026-10-13):
 * `recordHeadline()` names a human visit "Visitor from <City>" via
 * `humanRecordName`, and the device line is `humanRecordDevice` ("Chrome
 * on Windows"). Kept for callers that need the version.
 */
export function browserShortLabel(user_agent) {
    if (user_agent === undefined)
        return 'Browser visit';
    // Order matters — Edg before Chrome (Edge UAs include both); Opera
    // includes "Chrome" too; Safari only matches when Chrome is absent.
    const edge = /\bEdg(?:e)?\/(\d+)/.exec(user_agent);
    if (edge !== null)
        return `Browser visit · Edge ${edge[1]}`;
    const opera = /\b(?:OPR|Opera)\/(\d+)/.exec(user_agent);
    if (opera !== null)
        return `Browser visit · Opera ${opera[1]}`;
    const firefox = /\bFirefox\/(\d+)/.exec(user_agent);
    if (firefox !== null)
        return `Browser visit · Firefox ${firefox[1]}`;
    const chrome = /\bChrome\/(\d+)/.exec(user_agent);
    if (chrome !== null)
        return `Browser visit · Chrome ${chrome[1]}`;
    const safari = /\bVersion\/(\d+)[^/]*Safari/.exec(user_agent);
    if (safari !== null)
        return `Browser visit · Safari ${safari[1]}`;
    return 'Browser visit';
}
/**
 * Extract the bot name from a `Mozilla/5.0 (compatible; BotName/version; …)`
 * style UA — the dominant format for SEO/scraper bots (MJ12bot, AhrefsBot,
 * SemrushBot, DotBot, etc.). Returns just the bare name without the version
 * so it reads like "MJ12bot" not "MJ12bot/v1.4.8".
 *
 * Returns `null` when the UA doesn't follow this pattern (caller falls
 * through to `uaFirstToken`).
 */
export function extractCompatibleBotName(user_agent) {
    if (user_agent === undefined)
        return null;
    // Match: (compatible; <Name>/version …) OR (compatible; <Name>; url) —
    // some bots omit the version entirely (Bytespider, PetalBot) and go
    // straight to a URL after the name, so we accept either `/` or `;`/`)`
    // as the delimiter following the name.
    const m = /\(compatible;\s*([A-Za-z][A-Za-z0-9._-]{1,40})(?:\/[^\s;)]|[;)])/i.exec(user_agent);
    if (m === null)
        return null;
    const name = m[1] ?? '';
    // Suppress generic strings that aren't a real bot name.
    if (/^Mozilla$/i.test(name))
        return null;
    return name.length > 32 ? `${name.slice(0, 32)}…` : name;
}
const GENERIC_UA_TOKENS = new Set(['mozilla', 'applewebkit', 'khtml', 'gecko', 'chrome', 'safari', 'version', 'mobile', 'firefox', 'edge', 'edg', 'opr', 'trident', 'windows', 'linux', 'x11', 'crios', 'fxios', 'like', 'chromium', 'wv', 'samsungbrowser', 'ucbrowser', 'yabrowser']);
const BOTISH_RE = /bot|crawl|spider|fetch|agent|scan|preview|monitor|check|feed|reader|validator|scraper|search|index|archive|link|externalhit|inspect|audit|probe|ping|http|client|request|curl|wget|python|java|okhttp|node|go-|axios|postman|insomnia|dart|ruby|perl|php|libwww|apache|guzzle|symfony|aiohttp|httpx|urllib|scrapy/i;
/**
 * The bot's own name from its user-agent, or null when the UA carries
 * nothing a person would recognise. Order:
 *   1. `(compatible; Name/…)` — SEO and search crawlers
 *   2. any `Name/version` token that reads like a bot or an HTTP client
 *      (GPTBot/1.0, feedparser/6, node-fetch/2, Go-http-client/1.1)
 *   3. a non-Mozilla UA's first token (curl, python-requests, Wget)
 * Browser engine tokens (Mozilla, AppleWebKit, Chrome…) never count.
 */
export function botNameFromUserAgent(user_agent) {
    if (user_agent === undefined)
        return null;
    const ua = user_agent.trim();
    if (ua.length === 0)
        return null;
    const compatible = extractCompatibleBotName(ua);
    if (compatible !== null)
        return compatible;
    const tokens = [...ua.matchAll(/([A-Za-z][A-Za-z0-9._+-]{1,40})\/[A-Za-z0-9._+-]{1,40}/g)].map((m) => m[1] ?? '');
    const candidates = tokens.filter((t) => !GENERIC_UA_TOKENS.has(t.toLowerCase()));
    const botish = candidates.find((t) => BOTISH_RE.test(t));
    if (botish !== undefined)
        return botish.length > 32 ? `${botish.slice(0, 32)}…` : botish;
    if (!/^Mozilla\//i.test(ua) && candidates.length > 0) {
        const first = candidates[0];
        return first.length > 32 ? `${first.slice(0, 32)}…` : first;
    }
    // A bare word UA ("yak", "Reflectionbot") with no version.
    if (!/^Mozilla\//i.test(ua) && /^[A-Za-z][A-Za-z0-9._+-]{1,40}$/.test(ua))
        return ua;
    return null;
}
/**
 * First "name/version" token from a UA — used in `recordHeadline()` for
 * `unknown_agent` records with no recognizable token, so the card at
 * least carries the bot's self-identifier (e.g. `req/v3`, `curl/8.0`).
 * Prefers the `compatible;` bot name for Mozilla-wrapped UAs (SEO bots).
 * Trimmed to 24 chars to keep the headline scannable.
 */
export function uaFirstToken(user_agent) {
    if (user_agent === undefined)
        return null;
    // For "Mozilla/5.0 (compatible; BotName/…)" prefer the inner bot name.
    const compatible = extractCompatibleBotName(user_agent);
    if (compatible !== null)
        return compatible;
    const match = /([A-Za-z][A-Za-z0-9._-]{0,32}\/[A-Za-z0-9._-]{1,32})/.exec(user_agent);
    if (match === null)
        return null;
    const token = match[1] ?? '';
    return token.length > 24 ? `${token.slice(0, 24)}…` : token;
}
/**
 * Headline string for an audit row. Single source of truth — the
 * Mandates list view renders this on each card, and the search filter
 * matches against the same string so "what you see is what you can
 * search for". Headlines are sentence case (not mono-uppercase) and
 * drop the "visit observed" suffix — the OBSERVED decision pill and
 * the audit-list context already carry "this is an observation,"
 * so the headline can lead with identity instead.
 */
/**
 * Read the ACTUAL settled refund amount off a wire refund record's
 * `settlement.refunded_minor` (write-gated server-side to succeeded /
 * reconciled outcomes only — a failed/aborted refund never carries it).
 * Returns a formatted string ("$42.59"), or `null` when the refund has
 * not settled (so the headline falls back to the signed cap). Defensive
 * reader through `unknown` — `settlement` lives on the dashboard-local
 * wire extension, not the canonical signed `AuditRecord`.
 */
function refundedAmountForHeadline(record) {
    const settlement = record.settlement;
    if (settlement === null || typeof settlement !== 'object')
        return null;
    const s = settlement;
    if (s['kind'] !== 'refund')
        return null;
    const refunded_raw = s['refunded_minor'];
    if (typeof refunded_raw !== 'number')
        return null;
    const currency_raw = s['currency'];
    const currency = typeof currency_raw === 'string' && currency_raw.length > 0 ? currency_raw : 'USD';
    return formatMinorAmount(refunded_raw, currency);
}
/**
 * ADR-0099 — refund-record headline, identical in shape to the Resolution
 * card's `refundHeadline`: `Refund request · <Reason> · <amount>`. The
 * amount is the ACTUAL refunded total when settled, else the agent-signed
 * cap framed as "up to $X" (the refund is bounded by the cap, never
 * asserted by it). `reason_code` is read defensively — it's projected onto
 * the wire intent_summary but is not declared on the canonical type.
 */
function refundRecordHeadline(record, intent) {
    const reason_code = intent.reason_code;
    const reason = typeof reason_code === 'string' && reason_code.length > 0
        ? humanizeReasonCode(reason_code)
        : 'Unspecified reason';
    const refunded = refundedAmountForHeadline(record);
    const amount = refunded ?? `up to ${formatIntentAmount(intent.max_amount, intent.currency)}`;
    return `Refund request · ${reason} · ${amount}`;
}
/**
 * ADR-0234 — Sill-enrolled operator "who" branch. Shared by the mandate
 * list (`recordHeadline` below), the dashboard detail panel
 * (`MandateDetailV3.resolveAgentHeadline`) and the signed audit-bundle
 * HTML export so the three surfaces never disagree on a signed visit.
 *
 *   - Case A — `signed_credential_name_registered === true`: show the
 *     operator-registered agent display name ("Sill Readiness") with the
 *     operator's own display name ("sill-agents") suffixed in a bullet
 *     separator. The merchant sees who the operator says this is, not
 *     the raw UA token.
 *   - Case B — `signed_credential_name_registered === false`: show the
 *     UA token as sent (so the merchant can see what the agent CLAIMED
 *     to be) and append "name not registered to <operator>" so the row
 *     carries the ADR-0234 safety note. Never asserts a verified identity
 *     of the token.
 *   - Case where the flag is absent (verified Sill signature but the
 *     server did not evaluate the registration — e.g. snapshot read
 *     failed): a plain "Signed by <operator>" without claiming the UA
 *     name is registered.
 *
 * Returns `null` when `record` is NOT a verified Sill-credential visit.
 * The caller (`recordHeadline`, `resolveAgentHeadline`, export renderer)
 * falls through to its existing branches in that case.
 */
/**
 * Pull the first PRODUCT NAME from a UA — the "AcmeBot" out of
 * "AcmeBot/1.0 (+https://…)" or the "GPTBot" out of
 * "Mozilla/… (compatible; GPTBot/1.4; +…)". Strips the `/version` tail
 * that `uaFirstToken` keeps, because the Sill-credential headline wants
 * the bare product name (the version is noise there).
 */
function uaProductName(user_agent) {
    const compatible = extractCompatibleBotName(user_agent);
    if (compatible !== null)
        return compatible;
    const token = uaFirstToken(user_agent);
    if (token === null)
        return null;
    const slash = token.indexOf('/');
    return slash > 0 ? token.slice(0, slash) : token;
}
export function sillCredentialWhoLabel(record) {
    if (record.signature_verified !== true)
        return null;
    const signer = (record.signed_agent ?? '').trim().toLowerCase();
    if (!signer.startsWith('edge.sill.so/agents/'))
        return null;
    // 2026-10-12 — Sill's own tools signed with Sill's first-party key: the
    // registered name already says Sill ("Sill · Site scanner").
    if (isSillFirstPartySigner(signer)
        && record.signed_credential_name_registered === true
        && typeof record.signed_credential_agent_display === 'string'
        && record.signed_credential_agent_display.length > 0) {
        return record.signed_credential_agent_display;
    }
    const operator = typeof record.signed_operator_display === 'string' && record.signed_operator_display.length > 0
        ? record.signed_operator_display
        : (typeof record.signed_operator_slug === 'string' && record.signed_operator_slug.length > 0
            ? record.signed_operator_slug
            : 'a Sill-enrolled operator');
    if (record.signed_credential_name_registered === true) {
        const agent_name = typeof record.signed_credential_agent_display === 'string'
            && record.signed_credential_agent_display.length > 0
            ? record.signed_credential_agent_display
            : (uaProductName(record.user_agent) ?? operator);
        return `${agent_name} · ${operator}`;
    }
    if (record.signed_credential_name_registered === false) {
        const name = uaProductName(record.user_agent) ?? 'Signed visit';
        return `${name} · signed by ${operator} (name not registered)`;
    }
    // Verified Sill signature but the registration check was not evaluated
    // (snapshot read failed / older row). Carry the operator so the surface
    // still carries something stronger than "Unidentified visitor", without
    // asserting the agent name is registered.
    const token = uaProductName(record.user_agent);
    if (token !== null)
        return `${token} · signed by ${operator}`;
    return `Signed by ${operator}`;
}
/**
 * ADR-0214 §5 — merchant-facing name for a verified signed visit from
 * a signer OUTSIDE the Sill-hosted directory. Shared with
 * `@sill/types.publicSignerDisplayFor` so the list, the dashboard
 * detail panel and the bundle HTML export read ONE source of truth.
 *
 * Security review 2026-10-11 MINOR 2: the dashboard must refuse to
 * promote a signed visit whose UA token the signer does NOT
 * legitimately ship. A valid signature only proves control of the
 * signer host — it does not cover the user-agent. So a chatgpt.com-
 * signed request with UA `"GPTBot/1.0"` must NOT read as "ChatGPT"
 * (two different OpenAI products, different trust path), and an
 * evil.example-signed request with a ChatGPT UA must not read as
 * "ChatGPT" either. The `allowed_token_re` gate lives in
 * `KNOWN_PUBLIC_SIGNERS`.
 */
export function publicSignerWhoLabel(record) {
    if (record.signature_verified !== true)
        return null;
    const signer = (record.signed_agent ?? '').trim();
    if (signer.length === 0)
        return null;
    if (signer.toLowerCase().startsWith('edge.sill.so/agents/'))
        return null;
    return publicSignerDisplayFor(signer, record.user_agent);
}
export function recordHeadline(record) {
    if (record.intent_summary !== undefined) {
        // ADR-0099 — a refund record gets the SAME headline shape as the
        // Resolution card (`Refund request · <Reason> · <amount>`) so the two
        // surfaces never disagree. The amount is the ACTUAL refunded total
        // when the refund settled; otherwise the agent-signed cap framed as
        // "up to" (never assert a refund that didn't happen).
        if (record.intent_summary.action === 'request_refund') {
            return refundRecordHeadline(record, record.intent_summary);
        }
        // Prefer the actual settled-rail total over the agent-signed cap when
        // the rail dispatched a payment-rail settlement. The cap (`max_amount`)
        // is what the agent was AUTHORIZED to spend; the settled total is what
        // the buyer was ACTUALLY charged — those are usually different, and
        // surfacing the cap as the headline reads as a discrepancy against the
        // SETTLEMENT panel below. `null` (no settlement OR malformed total)
        // falls back to the cap so non-settled mandates still render.
        const settled = settledTotalForHeadline(record);
        return summarizeIntent(record.intent_summary, settled);
    }
    if (record.decision === 'observed') {
        const cls = record.identification_match?.visitor_class;
        const agent_id = record.identification_match?.agent_id;
        // ADR-0234 — a verified Sill-credential signature is the strongest
        // identity on an observation record. It outranks the bundled registry
        // match + the UA-token fallback so Case A reads "Sill Readiness ·
        // sill-agents" (operator-registered name) and Case B reads
        // "NotRegisteredBot · signed by sill-agents (name not registered)".
        // ADR-0214 §5 — a verified public signer (chatgpt.com, …) outranks
        // the generic UA token path too, so a ChatGPT-User UA sent from
        // ChatGPT reads "ChatGPT" rather than the browser string.
        const sill_who = sillCredentialWhoLabel(record);
        const public_who = sill_who === null ? publicSignerWhoLabel(record) : null;
        // Security review 2026-10-11 MAJOR 1: when a signature verified
        // but the signer+UA pair does NOT match either a Sill-credential or
        // a KNOWN_PUBLIC_SIGNERS entry, the bundled-registry agent_id
        // branch below would echo the UA-claimed display name under a
        // mismatched signer (e.g. "OpenAI · GPTBot" for a chatgpt.com
        // signer with a GPTBot UA, or a Sill operator signing with a
        // GPTBot UA). Refuse that: fall through to a "signed by <signer>"
        // headline that carries the signer host without asserting the
        // UA-claimed identity.
        const signed_unvetted = (() => {
            if (sill_who !== null || public_who !== null)
                return null;
            if (record.signature_verified !== true)
                return null;
            const signer = (record.signed_agent ?? '').trim();
            if (signer.length === 0)
                return null;
            const token = uaFirstToken(record.user_agent);
            if (token !== null && token.length > 0)
                return `${token} · signed by ${signer}`;
            return `Signed by ${signer}`;
        })();
        // Compute the "who" label (the agent / visitor identity) first; the
        // connector-skill prefix below decides whether to lead with "what".
        let who;
        if (sill_who !== null) {
            who = sill_who;
        }
        else if (public_who !== null) {
            who = public_who;
        }
        else if (signed_unvetted !== null) {
            who = signed_unvetted;
        }
        else if (cls === 'matched_agent') {
            // Drop the org prefix when present (the badge color carries org);
            // keep the bot's short name as the headline.
            who = headlineAgentName(agent_id);
        }
        else if (cls === 'unknown_agent') {
            // Client-side inference: if the UA visibly contains a known token,
            // promote it to "Likely <name>" so the headline is informative.
            const inferred = inferAgentFromUserAgent(record.user_agent);
            if (inferred !== null && SILL_INTERNAL_AGENT_IDS.has(inferred)) {
                // Sill's own tools always read "Sill · <tool>", never "Likely …"
                // (founder feedback 2026-10-14).
                who = displayAgentName(inferred);
            }
            else if (inferred !== null) {
                // Founder bug 2026-10-18: dropping the org prefix made
                // "Likely External Fetcher (user)" read as something unrelated
                // to the "a Meta AI user action fetched your page" description.
                // Keep the full display name so the headline carries the
                // operator the description relies on
                // ("Likely Meta · External Fetcher (user)").
                who = `Likely ${displayAgentName(inferred)}`;
            }
            else {
                // Bots using the standard (compatible; BotName; …) format self-declare
                // their identity — show the extracted name directly without the
                // "Unknown bot ·" prefix so unregistered crawlers still read cleanly.
                const compatibleName = extractCompatibleBotName(record.user_agent);
                if (compatibleName !== null) {
                    who = compatibleName;
                }
                else {
                    const token = uaFirstToken(record.user_agent);
                    who = token !== null ? `Unknown bot · ${token}` : 'Unknown bot';
                }
            }
        }
        else if (cls === 'human_likely') {
            // "Visitor from Pembroke Pines" — the same name the per-person rows
            // use (ADR-0243). The browser + OS line is `humanRecordDevice`.
            who = humanRecordName(record);
        }
        else if (record.identification_match?.matched === true) {
            // Legacy records (pre-2026-06-01) lack visitor_class. Fall back to
            // the matched/unmatched binary so they don't render as "Unknown bot".
            who = headlineAgentName(agent_id);
        }
        else {
            who = 'Agent visit observed';
        }
        // ADR-0037: when the observation is a bridged-connector tool call,
        // lead with WHAT was called (the skill) so the card says what the
        // call was FOR — not just who made it. The friendly label
        // ("Catalog browse") already exists; the headline just never used it
        // for connector records. Pure embed-beacon observations (no
        // connector_call) keep the identity-only headline unchanged.
        //
        // 2026-06-04 MCP audit fix: the unified top-level `record.skill_id`
        // is the new source of truth — it COALESCEs the wired
        // `connector_call.skill_id` with the unwired MCP path's
        // `identification_input.mcp.tool_name`, so BOTH MCP record kinds
        // ("Catalog browse · …" / "Availability check · …") render with a
        // skill prefix. `connector_call?.skill_id` stays read for
        // backward-compat if a future writer somehow omits the top-level
        // field but still populates the sub-object's copy.
        const skill_id = record.skill_id ?? record.connector_call?.skill_id;
        if (skill_id !== undefined) {
            return `${friendlyActionLabel(skill_id)} · ${who}`;
        }
        return who;
    }
    if (record.decision === 'verification_rejected') {
        // 2026-06-04 MCP audit fix: prefer the sanitized `reject_reason` enum
        // (closed set, no PII — see `RejectReason` in
        // `services/api/src/queue/process-mandate-draft.ts`) when present so
        // the merchant sees WHY verification failed, not just THAT it failed.
        if (typeof record.reject_reason === 'string'
            && record.reject_reason.length > 0) {
            return `Signature verification failed · ${record.reject_reason}`;
        }
        return 'Signature verification failed';
    }
    return `Mandate ${outcomeLabel(record.decision)}`;
}
/**
 * Registry agent ids whose merchant-facing name contains `q`
 * (case-insensitive): the long form ("OpenAI · ChatGPT Search"), its
 * product part, the brand name and the humanized slug. The visits search
 * ORs these ids into its server-side match, so "ChatGPT Search" finds
 * `agent_openai_searchbot` (filters cleanup 2026-10-15). Fewer than two
 * characters match nothing (every name contains one letter).
 */
export function agentIdsMatchingName(q, extra_ids = []) {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2)
        return [];
    const ids = new Set([...Object.keys(AGENT_DISPLAY_NAME), ...Object.keys(AGENT_BRAND_NAME), ...BUNDLED_AGENT_IDS, ...SILL_INTERNAL_AGENT_IDS, ...extra_ids]);
    const out = [];
    for (const id of ids) {
        const long = displayAgentName(id);
        const names = [long, long.split(' · ').pop() ?? '', AGENT_BRAND_NAME[id] ?? ''];
        if (names.some((n) => n.toLowerCase().includes(needle)))
            out.push(id);
    }
    return out.sort();
}
//# sourceMappingURL=labels.js.map